/*
  One add-to-cart path for the whole theme.

  CACart.add(items) posts to /cart/add.js with an items array (one or many
  lines in a single request) and resolves with Shopify's JSON response only
  when the request succeeded. A 422 (sold out, quantity over stock) or any
  other non-OK status rejects with Shopify's own description, so callers can
  show it instead of redirecting as if the add worked. Only one request runs
  at a time; a second tap while one is pending is ignored.

  A failed add is not always a no-op: preview testing (2026-09-22) showed
  Shopify adding the 24 units in stock and still returning 422 ("Only 24
  items were added to your cart due to availability."). So after any
  failure the header count is re-read from /cart.js.

  Buttons anywhere in the theme opt in with markup, no per-section script:
    data-ca-add-to-cart data-variant-id="123" [data-quantity="2"]
    data-ca-add-all-to-cart data-variant-ids="123,456"
  On success the shopper goes to /cart. On failure the button is re-enabled
  and the message appears in a role="alert" line after it.
*/
(function () {
  if (window.CACart) return;

  var pending = false;
  var GENERIC_ERROR = 'Could not add to cart. Please try again.';

  // Line properties (Kaching's deal tag, 2026-09-25) and a parent line
  // (Shopify's nested cart lines, used for a deal's free gift) pass through.
  function toItems(list) {
    return list
      .map(function (item) {
        var out = {
          id: parseInt(item.id, 10),
          quantity: Math.max(1, parseInt(item.quantity, 10) || 1)
        };
        if (item.properties) out.properties = item.properties;
        if (item.parent_id) out.parent_id = parseInt(item.parent_id, 10);
        return out;
      })
      .filter(function (item) { return item.id > 0; });
  }

  function refreshCount() {
    // Also announces the cart (ca:cart:updated) so the product page's free
    // shipping bar can count what is already in it, after discounts.
    var els = document.querySelectorAll('[data-ca-cart-count]');
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        els.forEach(function (el) { el.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('ca:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {});
  }

  function add(list) {
    var items = toItems(list || []);
    if (!items.length) return Promise.reject({ message: GENERIC_ERROR });
    if (pending) return Promise.reject({ message: '', busy: true });
    pending = true;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) {
            throw { message: body.description || body.message || GENERIC_ERROR, status: res.status };
          }
          return body;
        });
      }, function () {
        throw { message: GENERIC_ERROR };
      })
      .then(function (body) {
        pending = false;
        document.dispatchEvent(new CustomEvent('ca:cart:added', { detail: { items: items, response: body } }));
        return body;
      }, function (err) {
        pending = false;
        refreshCount();
        throw err;
      });
  }

  function messageFor(button) {
    var id = button.getAttribute('data-ca-cart-error-target');
    if (id) return document.getElementById(id);
    var next = button.nextElementSibling;
    if (next && next.hasAttribute('data-ca-cart-error')) return next;
    var el = document.createElement('p');
    el.className = 'ca-cart-error';
    el.setAttribute('data-ca-cart-error', '');
    el.setAttribute('role', 'alert');
    el.hidden = true;
    button.insertAdjacentElement('afterend', el);
    return el;
  }

  function showError(el, message) {
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function setBusy(buttons, busy) {
    buttons.forEach(function (btn) {
      if (!btn) return;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      } else {
        btn.removeAttribute('aria-busy');
      }
    });
  }

  function itemsFromButton(btn) {
    if (btn.hasAttribute('data-ca-add-all-to-cart')) {
      return (btn.getAttribute('data-variant-ids') || '')
        .split(',')
        .filter(Boolean)
        .map(function (id) { return { id: id, quantity: 1 }; });
    }
    return [{ id: btn.getAttribute('data-variant-id'), quantity: btn.getAttribute('data-quantity') || 1 }];
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ca-add-to-cart], [data-ca-add-all-to-cart]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var errorEl = messageFor(btn);
    showError(errorEl, '');
    setBusy([btn], true);
    add(itemsFromButton(btn)).then(function () {
      window.location.href = '/cart';
    }, function (err) {
      setBusy([btn], false);
      btn.disabled = false;
      if (err && err.busy) return;
      showError(errorEl, err && err.message);
    });
  });

  window.CACart = { add: add, showError: showError, setBusy: setBusy, refreshCount: refreshCount };
})();
